import dayjs from 'dayjs';
import { amplitudeLib } from 'freedom-shared';
import { dateUtils, type MappedLocation } from 'shared';
import { airflow } from '@aviata/platform-client';
import { omit } from 'lodash-es';

import { extractAnalyticsDataLib } from '../analytics-data-extraction';
import type {
  DetailsPageOpenParams,
  JjDetailsBannerShowParams,
} from '../analytics';
import { getSearchModeFromQuery, getMainPageVariant } from './main-page';

const mapDetailsPageOpenParams = (
  meta: DetailsPageOpenParams,
  locations: MappedLocation[],
) => {
  const { offer, index } = meta;
  const parsed = extractAnalyticsDataLib.offer(offer);

  const departureTimeFrom = parsed.departure?.time;
  const departureDateFrom = dayjs(
    dateUtils.removeTimezone(parsed.departure?.at),
  ).format('DD-MM-YYYY');

  const searchParams = airflow.utils.parseQuery(offer.$meta.search_query);
  const searchMode = getSearchModeFromQuery(searchParams);
  const isMultiTrip = searchParams?.ML === true;

  const lastFlight = isMultiTrip
    ? offer.flights[offer.flights.length - 1]
    : offer.flights[1];
  const departureBack = lastFlight?.segments[0]?.dep;
  let departureTimeBack: string | undefined = departureBack?.time;
  let departureDateBack: string | undefined =
    departureBack &&
    dayjs(dateUtils.removeTimezone(departureBack?.at)).format('DD-MM-YYYY');

  if (searchParams?.OW) {
    departureDateBack = undefined;
    departureTimeBack = undefined;
  }

  const itineraries = searchParams?.itineraries;
  const firstItinerary = itineraries?.[0];
  const lastItinerary = isMultiTrip
    ? itineraries?.[itineraries.length - 1]
    : undefined;

  const connectType = offer.$meta.connect_type?.toLowerCase() || 'false';

  const label = extractAnalyticsDataLib.getOfferDetailsOpenLabel(
    offer,
    meta.rankingTagsForLabel,
  );

  return {
    offer_position: index,
    airlines: parsed.airlines.map(a => a.iata),
    price: parsed.price,
    search_mode: searchMode,
    direct: parsed.isDirect,
    baggage: parsed.baggage,
    hand_luggage: parsed.handLuggage,
    city_to: lastItinerary?.arr ?? firstItinerary?.arr ?? locations[0]?.code,
    city_from: firstItinerary?.dep ?? locations[1]?.code,
    departure_date_from: departureDateFrom,
    departure_time_from: departureTimeFrom,
    departure_date_back: departureDateBack,
    departure_time_back: departureTimeBack,
    refundable: parsed.isRefundable,
    source: meta.source,
    is_charter: parsed.isCharter,
    travel_type: offer.$meta.is_domestic ? 'D' : 'I',
    sort_type: meta.sortType,
    connect_type:
      connectType === 'owc' || connectType === 'vi' ? connectType : 'false',
    label,
  };
};

export const detailsPageOpen = (
  params: DetailsPageOpenParams,
  locations: MappedLocation[],
) => {
  const payload = mapDetailsPageOpenParams(params, locations);
  amplitudeLib.logEvent('avfree_details_open', {
    ...payload,
    main_page_variant: getMainPageVariant(),
  });
};

export const detailsPageContinue = (
  params: DetailsPageOpenParams,
  locations: MappedLocation[],
) => {
  const payload = {
    ...omit(mapDetailsPageOpenParams(params, locations), 'offer_position'),
    ...(params.another_time_choosen !== undefined && {
      another_time_choosen: params.another_time_choosen,
    }),
    ...(params.another_airline_changed !== undefined && {
      another_airline_changed: params.another_airline_changed,
    }),
  };
  amplitudeLib.logEvent('avfree_details_continue_click', {
    ...payload,
    main_page_variant: getMainPageVariant(),
  });
};

export const jjDetailsBannerShow = (params: JjDetailsBannerShowParams) => {
  amplitudeLib.logEvent('avfree_jj_details_banner_show', params);
};
